import React from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/joy';
import {
  PencilSimple as EditIcon,
  CaretUp as MoveUpIcon,
  CaretDown as MoveDownIcon,
  Copy as DuplicateIcon,
  Trash as DeleteIcon,
  Sparkle as AIIcon,
} from '@phosphor-icons/react';
import type { FloatingToolbarProps } from '../../types/visualEditor';

export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  section,
  sectionConfig,
  bounds,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onAIGenerate,
  canMoveUp,
  canMoveDown,
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: -44,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: 'background.surface',
        borderRadius: '8px',
        boxShadow: 'md',
        p: 0.5,
        pointerEvents: 'auto',
        border: '1px solid',
        borderColor: 'divider',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Edit button */}
      <Tooltip title="Edit section" placement="top" size="sm">
        <IconButton
          size="sm"
          variant="plain"
          color="primary"
          onClick={onEdit}
          sx={{ borderRadius: '6px' }}
        >
          <EditIcon size={18} />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" sx={{ mx: 0.5 }} />

      {/* Move buttons */}
      <Tooltip title="Move up" placement="top" size="sm">
        <span>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            sx={{ borderRadius: '6px' }}
          >
            <MoveUpIcon size={18} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Move down" placement="top" size="sm">
        <span>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            sx={{ borderRadius: '6px' }}
          >
            <MoveDownIcon size={18} />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" sx={{ mx: 0.5 }} />

      {/* Duplicate button */}
      <Tooltip title="Duplicate section" placement="top" size="sm">
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={onDuplicate}
          sx={{ borderRadius: '6px' }}
        >
          <DuplicateIcon size={18} />
        </IconButton>
      </Tooltip>

      {/* AI Generate button */}
      <Tooltip title="AI Generate content" placement="top" size="sm">
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={onAIGenerate}
          sx={{
            borderRadius: '6px',
            '&:hover': {
              color: 'var(--brand-text, #7013f5)',
            },
          }}
        >
          <AIIcon size={18} />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" sx={{ mx: 0.5 }} />

      {/* Delete button */}
      <Tooltip title="Delete section" placement="top" size="sm">
        <IconButton
          size="sm"
          variant="plain"
          color="danger"
          onClick={onDelete}
          sx={{ borderRadius: '6px' }}
        >
          <DeleteIcon size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
